// Builds and parses the cron expressions used by check schedules. The frontend
// describes them; the backend (gorhill/cronexpr) evaluates occurrences and
// reminders. Recurring patterns use five
// fields; one-offs use seven fields with zero seconds and a fixed year.
// Occurrence evaluation belongs to the backend, including exhausted one-offs.
//
// Settings shape (weekdays use the UI's Monday-first 0-6 indexing):
// {
//     scheduleType: "2xhour" | "3xhour" | "hourly" | "daily" | "weekly" | "monthly",
//     time: "HH:MM",
//     weekdays: [0..6],
//     monthlyOption: "day" | "weekday",
//     dayOfMonth: 1..31,
//     monthlyOrdinal: 1..4 | -1 (last),
//     monthlyWeekday: 1..7 (Mon-Sun)
// }

function parseTimeOfDay(time) {
    const match = /^(\d{1,2}):(\d{2})$/.exec(time || "");
    if (!match) return { hour: 12, minute: 0 };
    return {
        hour: Math.min(23, Math.max(0, parseInt(match[1], 10))),
        minute: Math.min(59, Math.max(0, parseInt(match[2], 10)))
    };
}

// Returns the cron string for the settings, or "" when the settings are
// incomplete (e.g. weekly with no days selected).
export function buildScheduleCron(settings) {
    const { hour, minute } = parseTimeOfDay(settings.time);

    switch (settings.scheduleType) {
        case "once": {
            if (!/^\d{4}-\d{2}-\d{2}$/.test(settings.date || "")) return "";
            if (!/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(settings.time || "")) return "";
            const date = new Date(`${settings.date}T00:00:00Z`);
            if (
                !Number.isFinite(date.getTime()) ||
                date.toISOString().slice(0, 10) !== settings.date
            )
                return "";
            const [year, month, day] = settings.date.split("-").map(Number);
            if (year < 1970 || year > 2099) return "";
            return `0 ${minute} ${hour} ${day} ${month} * ${year}`;
        }
        case "2xhour":
            return "*/30 * * * *";
        case "3xhour":
            return "*/20 * * * *";
        case "hourly":
            return "0 * * * *";
        case "daily":
            return `${minute} ${hour} * * *`;
        case "weekly": {
            if (!settings.weekdays?.length) return "";
            const days = [...settings.weekdays].sort((a, b) => a - b).map((day) => day + 1);
            return `${minute} ${hour} * * ${days.join(",")}`;
        }
        case "monthly": {
            if (settings.monthlyOption === "weekday") {
                const weekday = settings.monthlyWeekday ?? 1;
                const dayOfWeek =
                    settings.monthlyOrdinal === -1
                        ? `${weekday}L`
                        : `${weekday}#${settings.monthlyOrdinal ?? 1}`;
                return `${minute} ${hour} * * ${dayOfWeek}`;
            }
            const dayOfMonth = Math.min(31, Math.max(1, settings.dayOfMonth ?? 1));
            return `${minute} ${hour} ${dayOfMonth} * *`;
        }
        default:
            return "";
    }
}

// Maps a cron expression back to editor settings, or null when the
// expression uses a pattern the editor can't represent (the caller should
// warn that saving will replace it).
export function parseScheduleCron(cron) {
    if (typeof cron !== "string") return null;
    const parts = cron.trim().split(/\s+/);
    if (parts.length === 7 && parts[0] === "0" && parts[5] === "*") {
        const [, minute, hour, day, month, , year] = parts;
        if (
            ![minute, hour, day, month].every((part) => /^\d{1,2}$/.test(part)) ||
            !/^\d{4}$/.test(year)
        )
            return null;
        const settings = {
            scheduleType: "once",
            date: `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`,
            time: `${hour.padStart(2, "0")}:${minute.padStart(2, "0")}`
        };
        return buildScheduleCron(settings) ? settings : null;
    }
    if (parts.length !== 5) return null;

    const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;
    if (month !== "*") return null;

    if (minute === "*/30" && hour === "*") return { scheduleType: "2xhour" };
    if (minute === "*/20" && hour === "*") return { scheduleType: "3xhour" };
    if (minute === "0" && hour === "*" && dayOfMonth === "*" && dayOfWeek === "*") {
        return { scheduleType: "hourly" };
    }

    if (!/^\d{1,2}$/.test(minute) || !/^\d{1,2}$/.test(hour)) return null;
    const time = `${hour.padStart(2, "0")}:${minute.padStart(2, "0")}`;

    if (dayOfWeek !== "*") {
        if (dayOfMonth !== "*") return null;

        const nth = /^([1-7])#([1-4])$/.exec(dayOfWeek);
        if (nth) {
            return {
                scheduleType: "monthly",
                monthlyOption: "weekday",
                monthlyWeekday: parseInt(nth[1], 10),
                monthlyOrdinal: parseInt(nth[2], 10),
                time
            };
        }

        const last = /^([1-7])L$/.exec(dayOfWeek);
        if (last) {
            return {
                scheduleType: "monthly",
                monthlyOption: "weekday",
                monthlyWeekday: parseInt(last[1], 10),
                monthlyOrdinal: -1,
                time
            };
        }

        if (/^[0-7](,[0-7])*$/.test(dayOfWeek)) {
            const weekdays = [
                ...new Set(
                    dayOfWeek
                        .split(",")
                        .map((day) => (parseInt(day, 10) === 0 ? 7 : parseInt(day, 10)) - 1)
                )
            ].sort((a, b) => a - b);
            return { scheduleType: "weekly", weekdays, time };
        }

        return null;
    }

    if (dayOfMonth !== "*") {
        if (!/^\d{1,2}$/.test(dayOfMonth)) return null;
        return {
            scheduleType: "monthly",
            monthlyOption: "day",
            dayOfMonth: parseInt(dayOfMonth, 10),
            time
        };
    }

    return { scheduleType: "daily", time };
}
